import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { DisplayMode } from '../../shared/display-mode';
import { ModalService } from '../../shared/services/modal.service';
import { LoaderService } from '../../shared/services/loader.service';
import { NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { NavigationService } from '../../shared/services/navigation.service';
import { Report } from '../shared/report.model';
import { ActivatedRoute, Params } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ReportService } from '../shared/report.service';
import { finalize } from 'rxjs/operators';
import { RegionService } from '../../user-dashboard/shared/region.service';
import { Region } from '../../user-dashboard/shared/region.model';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';

@Component({
  selector: 'app-report-form',
  templateUrl: './report-form.component.html',
  styleUrls: ['./report-form.component.scss']
})
export class ReportFormComponent implements OnInit {

  isActionDropdownOpen = false;

  report: Report = new Report();

  formProcessingBlocked = false;

  mode = DisplayMode.Creation;

  _displayMode_ = DisplayMode;

  allRegions: Region[] = [];

  @ViewChild('myForm')
  form: NgForm;

  constructor(
    private route: ActivatedRoute,
    public location: Location,
    private modalService: ModalService,
    private loaderService: LoaderService,
    private toastr: ToastrService,
    private navigation: NavigationService,
    private reportService: ReportService,
    private regionService: RegionService,
    private translate: TranslateService,
    private errorHandler: ErrorHandlerService,
  ) {

    this.route.params.subscribe((params: Params): void => {
      const reportId = params.reportId;

      if (reportId) {
        this.loaderService.startLoad(false);

        this.reportService.getById(reportId)
          .pipe(finalize(() => this.loaderService.stopLoad()))
          .subscribe(report => {
            this.report = report;

            this.switchToUpdateMode();
          }, e => this.errorHandler.handleHttpError(e, 'reports.form.errors.fetch'));
      }
    });
  }

  ngOnInit() {
    this.regionService.getAllLight().subscribe((regions: Region[]) => {
      this.allRegions = regions;
    });
  }

  getForm() {
    return this.form;
  }

  switchToUpdateMode(): void {
    this.form.form.markAsPristine();
    this.mode = DisplayMode.Edition;
  }

  processForm(): void {
    this.formProcessingBlocked = true;
    if (this.mode === DisplayMode.Creation) {
      this.create();
    } else {
      this.update();
    }
  }

  create() {
    this.loaderService.startLoad();
    this.report.homeReport = false;
    this.reportService.create(this.report)
      .pipe(finalize(() => {
        this.loaderService.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe((result: Report) => {
        this.report = result;
        this.navigation.reportsConfiguration()
          .list()
          .go();
        this.toastr.success(this.translate.instant('reports.form.successes.create'));
      }, (error: HttpErrorResponse) => {
        this.toastr.error(error.error.message || this.translate.instant('reports.form.errors.create'));
      })
  }

  update() {
    this.loaderService.startLoad();

    this.reportService.update(this.report)
      .pipe(finalize(() => {
        this.loaderService.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe((result: Report) => {
        this.form.form.markAsPristine();
        this.report = result;
        this.navigation.reportsConfiguration()
          .list()
          .go();
        this.toastr.success(this.translate.instant('reports.form.successes.update'));
      }, (error: HttpErrorResponse) => {
        this.toastr.error(error.error.message || this.translate.instant('reports.form.errors.update'));
      })
  }

  openConfirmDelete(content) {
    this.modalService.open({
      title: this.translate.instant('reports.form.modals.delete.title'),
      content: content,
      closeButton: {
        text: this.translate.instant('reports.form.modals.delete.buttons.no'),
      },
      validateButton: {
        type: 'danger',
        text: this.translate.instant('reports.form.modals.delete.buttons.yes'),
        icon: 'fa fa-trash'
      },
      size: 'sm'
    }).subscribe(() => this.doDeleteReport());
  }

  private doDeleteReport(): void {
    this.loaderService.startLoad();
    this.formProcessingBlocked = true;

    this.reportService.delete(this.report.id)
      .pipe(finalize(() => {
        this.loaderService.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe(() => {
        this.form.form.markAsPristine();
        this.toastr.success(this.translate.instant('reports.form.successes.delete'));
        this.navigation.reportsConfiguration()
          .list()
          .go();
      }, e => this.errorHandler.handleHttpError(e, 'reports.form.errors.delete'));
  }

}
